import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import invariant from 'tiny-invariant'

import referralFlowService from '@/lib/services/referralFlowService'
import {
  OUTLOOK_REFERRAL_FORM_INITIAL_VALUES,
  OutlookReferralFormValues,
} from '@/pages/officeTaskPane/components/OutlookReferral/constants'
import {
  getServiceAreasByIds,
  selectServiceAreas,
} from '@/redux/slices/commonData'
import { RootState } from '@/redux/store'
import { ClientItem } from '@/types/responses/clients'
import {
  ExistingAdviserReferralMetadataItem,
  NewAdviserReferralMetadataItem,
} from '@/types/responses/referral-metadata'
import { ReferralIndividualCollectionItem } from '@/types/responses/referrals'

interface OutlookReferralState {
  existingAdviserReferralMetadata?: ExistingAdviserReferralMetadataItem
  newAdviserReferralMetadata?: NewAdviserReferralMetadataItem
  formValues: OutlookReferralFormValues
  client: ClientItem | null
  isShowingOptionalClientFields: boolean
  isReferringToAdviserEmail: boolean
  hasEditedCommissionJustification?: boolean
  isShowingRequestApprovalModal?: boolean
  approvalRequestMessage?: string
  existingAdvisersWithEmail: ReferralIndividualCollectionItem[]
  hasUserOverriddenOutlook: boolean
  userEmailMessage?: string
  shouldShowOutdatedEmailNote: boolean
  hasCustomisedEmailMessage: boolean
}

export const OUTLOOK_REFERRAL_INITIAL_STATE: OutlookReferralState = {
  formValues: OUTLOOK_REFERRAL_FORM_INITIAL_VALUES,
  client: null,
  isReferringToAdviserEmail: false,
  isShowingOptionalClientFields: false,
  existingAdvisersWithEmail: [],
  hasUserOverriddenOutlook: false,
  shouldShowOutdatedEmailNote: false,
  hasCustomisedEmailMessage: false,
}

const outlookReferral = createSlice({
  name: 'outlookReferral',
  initialState: OUTLOOK_REFERRAL_INITIAL_STATE,
  reducers: {
    resetOutlookReferral: () => {
      return OUTLOOK_REFERRAL_INITIAL_STATE
    },

    setExistingAdviserReferralMetadata(
      state,
      action: PayloadAction<
        OutlookReferralState['existingAdviserReferralMetadata']
      >,
    ) {
      state.existingAdviserReferralMetadata = action.payload
    },

    setNewAdviserReferralMetadata(
      state,
      action: PayloadAction<OutlookReferralState['newAdviserReferralMetadata']>,
    ) {
      state.newAdviserReferralMetadata = action.payload
    },

    setFormValues(
      state,
      action: PayloadAction<OutlookReferralState['formValues']>,
    ) {
      state.formValues = action.payload
    },

    setClient(state, action: PayloadAction<OutlookReferralState['client']>) {
      state.client = action.payload
    },

    setIsReferringToAdviserEmail(
      state,
      action: PayloadAction<OutlookReferralState['isReferringToAdviserEmail']>,
    ) {
      state.isReferringToAdviserEmail = action.payload
    },

    setIsShowingOptionalClientFields(
      state,
      action: PayloadAction<
        OutlookReferralState['isShowingOptionalClientFields']
      >,
    ) {
      state.isShowingOptionalClientFields = action.payload
    },

    toggleIsShowingOptionalClientFields(state) {
      state.isShowingOptionalClientFields = !state.isShowingOptionalClientFields
    },

    setHasEditedCommissionJustification(
      state,
      action: PayloadAction<
        OutlookReferralState['hasEditedCommissionJustification']
      >,
    ) {
      state.hasEditedCommissionJustification = action.payload
    },

    showRequestApprovalModal: (state) => {
      state.isShowingRequestApprovalModal = true
    },

    hideRequestApprovalModal: (state) => {
      state.isShowingRequestApprovalModal = false
    },

    setApprovalRequestMessage(
      state,
      action: PayloadAction<OutlookReferralState['approvalRequestMessage']>,
    ) {
      state.approvalRequestMessage = action.payload
    },

    setExistingAdvisersWithEmail(
      state,
      action: PayloadAction<OutlookReferralState['existingAdvisersWithEmail']>,
    ) {
      state.existingAdvisersWithEmail = action.payload
    },

    // Stops the Outlook interceptor from overwriting the form values
    setHasUserOverriddenOutlook(state, action: PayloadAction<boolean>) {
      state.hasUserOverriddenOutlook = action.payload
    },

    setUserEmailMessage(
      state,
      action: PayloadAction<OutlookReferralState['userEmailMessage']>,
    ) {
      state.userEmailMessage = action.payload
    },

    setShouldShowOutdatedEmailNote(
      state,
      action: PayloadAction<
        OutlookReferralState['shouldShowOutdatedEmailNote']
      >,
    ) {
      state.shouldShowOutdatedEmailNote = action.payload
    },

    setHasCustomisedEmailMessage(
      state,
      action: PayloadAction<OutlookReferralState['hasCustomisedEmailMessage']>,
    ) {
      state.hasCustomisedEmailMessage = action.payload
    },
  },
})

export const selectOutlookReferral = (state: RootState) => state.outlookReferral

export const selectExistingAdviserReferralMetadata = createSelector(
  selectOutlookReferral,
  (state) => state.existingAdviserReferralMetadata,
)

export const selectExistingAdviserReferralMetadataOrFail = createSelector(
  selectExistingAdviserReferralMetadata,
  (metadata) => {
    invariant(metadata, 'Expected existingAdviserReferralMetadata to be set')

    return metadata
  },
)

export const selectNewAdviserReferralMetadata = createSelector(
  selectOutlookReferral,
  (state) => state.newAdviserReferralMetadata,
)

export const selectFormValues = createSelector(
  selectOutlookReferral,
  (state) => state.formValues,
)

export const selectReferralTypeOption = createSelector(
  selectFormValues,
  (formValues) => formValues.referralType,
)

export const selectIsMissingCommercialAgreement = createSelector(
  selectExistingAdviserReferralMetadata,
  (metadata) => metadata?.shouldAddOutgoingAgreement,
)

export const selectShouldCreateNoCommercialAgreementType = createSelector(
  selectIsMissingCommercialAgreement,
  selectFormValues,
  (isMissingCommercialAgreement, formValues) =>
    isMissingCommercialAgreement && formValues.hasCommercialAgreement === false,
)

export const selectClient = createSelector(
  selectOutlookReferral,
  (state) => state.client,
)

export const selectIsShowingOptionalClientFields = createSelector(
  selectOutlookReferral,
  (state) => state.isShowingOptionalClientFields,
)

export const selectReferralIndividuals = createSelector(
  selectFormValues,
  (formValues) => formValues.referralIndividuals,
)

export const selectIsReferringToAdviserEmail = createSelector(
  selectOutlookReferral,
  (state) => state.isReferringToAdviserEmail,
)

export const selectIsReferringToExistingAdviser = createSelector(
  selectIsReferringToAdviserEmail,
  (isReferringToAdviserEmail) => !isReferringToAdviserEmail,
)

export const selectReferralFirm = createSelector(
  selectFormValues,
  (formValues) =>
    formValues.company || formValues.referralIndividuals[0]?.company,
)

export const selectHasEditedCommissionJustification = createSelector(
  selectOutlookReferral,
  (state) => state.hasEditedCommissionJustification,
)

export const selectConsentType = createSelector(
  selectExistingAdviserReferralMetadata,
  (metadata) => referralFlowService.getConsentType(metadata),
)

export const selectRequiresColleagueApproval = createSelector(
  selectExistingAdviserReferralMetadata,
  selectNewAdviserReferralMetadata,
  (existingAdviserReferralMetadata, newAdviserReferralMetadata) =>
    !!existingAdviserReferralMetadata?.requiresColleagueApproval ||
    !!newAdviserReferralMetadata?.requiresColleagueApproval,
)

export const selectApprovalRequestMessage = createSelector(
  selectOutlookReferral,
  (state) => state.approvalRequestMessage,
)

export const selectIsShowingRequestApprovalModal = createSelector(
  selectOutlookReferral,
  (state) => state.isShowingRequestApprovalModal,
)

export const selectServiceAreaIds = createSelector(
  selectFormValues,
  (formValues) =>
    formValues.serviceAreas.map((serviceArea) => serviceArea.value.id),
)

export const selectSelectedServiceAreas = createSelector(
  selectServiceAreas(),
  selectServiceAreaIds,
  (serviceAreas, serviceAreaIds) => {
    return getServiceAreasByIds(serviceAreas, serviceAreaIds)
  },
)

export const selectExistingAdvisersWithEmail = createSelector(
  selectOutlookReferral,
  (state) => state.existingAdvisersWithEmail,
)

export const selectHasUserOverriddenOutlook = createSelector(
  selectOutlookReferral,
  (state) => state.hasUserOverriddenOutlook,
)

export const selectUserEmailMessage = createSelector(
  selectOutlookReferral,
  (state) => state.userEmailMessage,
)

export const selectShouldShowOutdatedEmailNote = createSelector(
  selectOutlookReferral,
  (state) => state.shouldShowOutdatedEmailNote,
)

export const selectHasCustomisedEmailMessage = createSelector(
  selectOutlookReferral,
  (state) => state.hasCustomisedEmailMessage,
)

export const {
  resetOutlookReferral,
  setExistingAdviserReferralMetadata,
  setNewAdviserReferralMetadata,
  setFormValues,
  setClient,
  setIsReferringToAdviserEmail,
  setIsShowingOptionalClientFields,
  toggleIsShowingOptionalClientFields,
  setApprovalRequestMessage,
  showRequestApprovalModal,
  hideRequestApprovalModal,
  setExistingAdvisersWithEmail,
  setHasUserOverriddenOutlook,
  setUserEmailMessage,
  setShouldShowOutdatedEmailNote,
  setHasCustomisedEmailMessage,
} = outlookReferral.actions

export const outlookReferralReducer = outlookReferral.reducer
